import { useInfiniteQuery, useAuthStore } from '@northladder/services';
import { usePAAS } from '@northladder/utilities';

import { BIDS_API } from '../bidsAPIClient';
import { IFetchAllBidsPaasData, IFetchAllBidsPayload } from '../types';

/**
 * This is to track the bids listing key for PAAS in react query cache.
 */
export const FETCH_ALL_BIDS_PAAS_KEY = 'fetch-all-bids-paas-key';

export interface IUseAllBidsSuccessCallbackProps {
  pages: IFetchAllBidsPaasData[];
}

/**
 * This performs a get request and retrieves the bids data by category and
 * brands.
 * Provides data fetching states to be used in the bids table.
 *
 * @deprecated - Remove this for a better PAAS integration.
 * - This feature is to be nuked out.
 */
export const usePaasSearchBidsQuery = (payload: IFetchAllBidsPayload) => {
  const { isPaasEnabled } = usePAAS();
  const {
    brandId,
    categoryId,
    count,
    filterBy,
    fromDate,
    groupIds,
    orderBy,
    page,
    search,
    sortBy,
    toDate,
  } = payload;

  const localPayload = {
    count,
    page,
    ...(brandId && brandId !== 'all' ? { brandId } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(filterBy ? { filterBy } : {}),
    ...(groupIds ? { groupIds } : {}),
    ...(orderBy ? { orderBy } : {}),
    ...(search ? { search } : {}),
    ...(sortBy ? { sortBy } : {}),
  };

  // TODO: Create a custom useInfinite query and update the key into there
  const userId = useAuthStore.getState()?.activeSession?.userId || '';

  const {
    data,
    isLoading,
    isPending,
    isError,
    error,
    isPaused,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      FETCH_ALL_BIDS_PAAS_KEY,
      categoryId,
      brandId,
      groupIds,
      toDate,
      fromDate,
      search,
      filterBy,
      sortBy,
      orderBy,
      userId,
    ],
    networkMode: 'always', // Even make calls when offline
    queryFn: ({ pageParam = 1 }) =>
      BIDS_API.searchAllBidsPaas({ ...localPayload, page: pageParam }),
    initialPageParam: 1,
    select: (response) => response,
    enabled: Boolean(isPaasEnabled) && Boolean(localPayload.categoryId),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;

      const currentAssetCount = allPages.length * count;

      return currentAssetCount < lastPage.totalCount ? nextPage : undefined;
    },
  });

  return {
    error,
    isLoading,
    isPending,
    isError,
    isPaused,
    data,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};
